import { useEffect, useState } from 'react';
import { Pressable, SafeAreaView, ScrollView, StyleSheet, Text, View, useWindowDimensions } from 'react-native';
import { Link, router } from 'expo-router';
import { Image } from 'expo-image';

import { AuthButton, AuthInput, AuthLinkRow, AuthNote, AuthScreen } from '@/components/auth/auth-ui';
import { Fonts } from '@/constants/theme';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/providers/auth-provider';
import slideOneSource from '../../assets/auth-slider/slide-1.webp';
import slideThreeSource from '../../assets/auth-slider/slide-3.webp';
import slideTwoSource from '../../assets/auth-slider/slide-2.webp';
import logoSource from '../../assets/images/mcqgen-logo-trimmed.png';

const desktopSlides = [
  slideOneSource,
  slideTwoSource,
  slideThreeSource,
];
const EMAIL_OTP_LENGTH = 8;

export default function SignUpScreen() {
  const { isConfigured } = useAuth();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [fullName, setFullName] = useState('');
  const [schoolName, setSchoolName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [authError, setAuthError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [desktopSlide, setDesktopSlide] = useState(0);
  const { width } = useWindowDimensions();
  const isDesktop = width >= 900;

  useEffect(() => {
    if (!isDesktop) {
      return;
    }

    const timer = setInterval(() => {
      setDesktopSlide((current) => (current + 1) % desktopSlides.length);
    }, 3200);

    return () => clearInterval(timer);
  }, [isDesktop]);

  const handleNextStep = () => {
    if (!fullName || !schoolName) {
      setAuthError('Enter your name and school name to continue.');
      return;
    }

    setAuthError(null);
    setStep(2);
  };

  const handleSignUp = async () => {
    if (!fullName || !schoolName || !email || !password || !confirmPassword) {
      setAuthError('Complete all fields before creating the account.');
      return;
    }

    if (password.length < 8) {
      setAuthError('Use at least 8 characters for the password.');
      return;
    }

    if (password !== confirmPassword) {
      setAuthError('The confirmation password does not match.');
      return;
    }

    if (!isConfigured) {
      setAuthError('Supabase is not configured. Restart the app and try again.');
      return;
    }

    setAuthError(null);
    setLoading(true);

    const { error } = await supabase.auth.signUp({
      email: email.trim(),
      password,
      options: {
        data: {
          full_name: fullName.trim(),
          school_name: schoolName.trim(),
        },
      },
    });

    setLoading(false);

    if (error) {
      setAuthError(error.message);
      return;
    }

    setOtpCode('');
    setStep(3);
  };

  const handleVerifyOtp = async () => {
    const normalizedEmail = email.trim();
    const normalizedOtp = otpCode.trim();

    if (!normalizedEmail || !normalizedOtp) {
      setAuthError(`Enter the ${EMAIL_OTP_LENGTH}-digit code sent to your email.`);
      return;
    }

    if (normalizedOtp.length !== EMAIL_OTP_LENGTH) {
      setAuthError(`Enter the full ${EMAIL_OTP_LENGTH}-digit OTP code.`);
      return;
    }

    if (!isConfigured) {
      setAuthError('Supabase is not configured. Restart the app and try again.');
      return;
    }

    setAuthError(null);
    setLoading(true);

    const { error } = await supabase.auth.verifyOtp({
      email: normalizedEmail,
      token: normalizedOtp,
      type: 'email',
    });

    setLoading(false);

    if (error) {
      setAuthError(error.message || 'The OTP code is invalid or expired.');
      return;
    }

    router.replace('/admin');
  };

  const handleResendOtp = async () => {
    const normalizedEmail = email.trim();

    if (!normalizedEmail) {
      setAuthError('Missing email address. Go back and enter the account email again.');
      return;
    }

    if (!isConfigured) {
      setAuthError('Supabase is not configured. Restart the app and try again.');
      return;
    }

    setAuthError(null);
    setResendLoading(true);

    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: normalizedEmail,
    });

    setResendLoading(false);

    if (error) {
      setAuthError(error.message || 'Unable to resend the OTP code right now.');
      return;
    }

    setAuthError('A new OTP code has been sent to your email.');
  };

  const clearAuthError = () => {
    if (authError) {
      setAuthError(null);
    }
  };

  if (!isDesktop) {
    return (
      <AuthScreen
        eyebrow="Create workspace"
        title="Set up your school account."
        subtitle="Add your school details, secure your login, then confirm the email OTP.">
        <View style={styles.stepHeader}>
          <View style={styles.stepNode}>
            <View style={[styles.stepPill, step === 1 && styles.stepPillActive]}>
              <Text style={[styles.stepPillText, step === 1 && styles.stepPillTextActive]}>1</Text>
            </View>
            <Text style={[styles.stepLabel, step === 1 && styles.stepLabelActive]}>Profile</Text>
          </View>
          <View style={styles.stepLine} />
          <View style={styles.stepNode}>
            <View style={[styles.stepPill, step === 2 && styles.stepPillActive]}>
              <Text style={[styles.stepPillText, step === 2 && styles.stepPillTextActive]}>2</Text>
            </View>
            <Text style={[styles.stepLabel, step === 2 && styles.stepLabelActive]}>Access</Text>
          </View>
          <View style={styles.stepLine} />
          <View style={styles.stepNode}>
            <View style={[styles.stepPill, step === 3 && styles.stepPillActive]}>
              <Text style={[styles.stepPillText, step === 3 && styles.stepPillTextActive]}>3</Text>
            </View>
            <Text style={[styles.stepLabel, step === 3 && styles.stepLabelActive]}>Confirm</Text>
          </View>
        </View>

        {step === 1 ? (
          <>
            <AuthInput
              autoCapitalize="words"
              autoComplete="name"
              label="Full name"
              onChangeText={(value) => {
                setFullName(value);
                clearAuthError();
              }}
              placeholder="Harun Bulbul"
              value={fullName}
            />
            <AuthInput
              autoCapitalize="words"
              label="School name"
              onChangeText={(value) => {
                setSchoolName(value);
                clearAuthError();
              }}
              placeholder="Bright Future Academy"
              value={schoolName}
            />
            <AuthButton onPress={handleNextStep} title="Next" />
          </>
        ) : step === 2 ? (
          <>
            <AuthInput
              autoCapitalize="none"
              autoComplete="email"
              keyboardType="email-address"
              label="Email"
              onChangeText={(value) => {
                setEmail(value);
                clearAuthError();
              }}
              placeholder="admin@school.com"
              value={email}
            />
            <AuthInput
              autoCapitalize="none"
              autoComplete="new-password"
              label="Password"
              onChangeText={(value) => {
                setPassword(value);
                clearAuthError();
              }}
              placeholder="Create a secure password"
              secureTextEntry
              value={password}
            />
            <AuthInput
              autoCapitalize="none"
              autoComplete="new-password"
              label="Confirm password"
              onChangeText={(value) => {
                setConfirmPassword(value);
                clearAuthError();
              }}
              placeholder="Repeat the password"
              secureTextEntry
              value={confirmPassword}
            />
            <View style={styles.actionGroup}>
              <View style={styles.actionItem}>
                <AuthButton onPress={() => setStep(1)} title="Back" variant="secondary" />
              </View>
              <View style={styles.actionItem}>
                <AuthButton loading={loading} onPress={handleSignUp} title="Create account" />
              </View>
            </View>
          </>
        ) : (
          <>
            <AuthNote tone="neutral">
              Enter the {EMAIL_OTP_LENGTH}-digit code sent to <Text style={styles.inlineEmail}>{email.trim()}</Text>.
            </AuthNote>
            <AuthInput
              autoCapitalize="none"
              autoComplete="one-time-code"
              keyboardType="number-pad"
              label="Email OTP"
              onChangeText={(value) => {
                setOtpCode(value.replace(/\D/g, '').slice(0, EMAIL_OTP_LENGTH));
                clearAuthError();
              }}
              placeholder="12345678"
              value={otpCode}
            />
            <View style={styles.actionGroup}>
              <View style={styles.actionItem}>
                <AuthButton onPress={() => setStep(2)} title="Back" variant="secondary" />
              </View>
              <View style={styles.actionItem}>
                <AuthButton loading={loading} onPress={handleVerifyOtp} title="Confirm account" />
              </View>
            </View>
            <AuthButton
              loading={resendLoading}
              onPress={handleResendOtp}
              title="Resend OTP"
              variant="secondary"
            />
          </>
        )}

        {authError ? <AuthNote tone="warn">{authError}</AuthNote> : null}

        <AuthNote tone="neutral">
          We use your school name to personalize the workspace and signup flow.
        </AuthNote>

        <AuthLinkRow
          actionHref="/sign-in"
          actionLabel="Sign in"
          prompt="Already have an account?"
        />

        <Link href="/" style={styles.secondaryLink}>
          Back to welcome
        </Link>
      </AuthScreen>
    );
  }

  return (
    <SafeAreaView style={styles.desktopSafeArea}>
      <View style={styles.desktopBg}>
        <View style={styles.desktopGlowOrbOne} />
        <View style={styles.desktopGlowOrbTwo} />
        <ScrollView contentContainerStyle={styles.desktopScroll} showsVerticalScrollIndicator={false}>
          <View style={styles.desktopShell}>
            <View style={styles.desktopFrame}>
              <View style={styles.desktopBrandPanel}>
                <View style={styles.desktopSliderStage}>
                  <View style={styles.desktopSliderImageWrap}>
                    <Image
                      contentFit="cover"
                      source={desktopSlides[desktopSlide]}
                      style={styles.desktopSliderImage}
                    />
                  </View>
                  <View style={styles.desktopSliderDots}>
                    {desktopSlides.map((_, index) => (
                      <Pressable
                        key={index}
                        onPress={() => setDesktopSlide(index)}
                        style={[
                          styles.desktopSliderDot,
                          index === desktopSlide && styles.desktopSliderDotActive,
                        ]}
                      />
                    ))}
                  </View>
                </View>
              </View>

              <View style={styles.desktopFormPanel}>
                <View style={styles.desktopCard}>
                  <View style={styles.desktopCardHeader}>
                    <View style={styles.desktopBrandRow}>
                      <View style={styles.desktopBrandMark}>
                        <Image contentFit="contain" source={logoSource} style={styles.desktopLogo} />
                      </View>
                      <View style={styles.desktopBrandCopy}>
                        <Text style={styles.desktopBrandKicker}>MCQGen</Text>
                        <Text style={styles.desktopBrandSub}>Assessment operations</Text>
                      </View>
                    </View>

                    <View style={styles.desktopHeadlineBlock}>
                      <Text style={styles.desktopEyebrow}>Create workspace</Text>
                      <Text style={styles.desktopTitle}>Sign up.</Text>
                      <Text style={styles.desktopSubtitle}>Set up your school account.</Text>
                    </View>
                  </View>

                  <View style={styles.desktopStepHeader}>
                    <View style={styles.stepNode}>
                      <View style={[styles.stepPill, step === 1 && styles.stepPillActive]}>
                        <Text style={[styles.stepPillText, step === 1 && styles.stepPillTextActive]}>1</Text>
                      </View>
                      <Text style={[styles.stepLabel, step === 1 && styles.stepLabelActive]}>Profile</Text>
                    </View>
                    <View style={styles.stepLine} />
                    <View style={styles.stepNode}>
                      <View style={[styles.stepPill, step === 2 && styles.stepPillActive]}>
                        <Text style={[styles.stepPillText, step === 2 && styles.stepPillTextActive]}>2</Text>
                      </View>
                      <Text style={[styles.stepLabel, step === 2 && styles.stepLabelActive]}>Access</Text>
                    </View>
                    <View style={styles.stepLine} />
                    <View style={styles.stepNode}>
                      <View style={[styles.stepPill, step === 3 && styles.stepPillActive]}>
                        <Text style={[styles.stepPillText, step === 3 && styles.stepPillTextActive]}>3</Text>
                      </View>
                      <Text style={[styles.stepLabel, step === 3 && styles.stepLabelActive]}>Confirm</Text>
                    </View>
                  </View>

                  {step === 1 ? (
                    <>
                      <AuthInput
                        autoCapitalize="words"
                        autoComplete="name"
                        label="Full name"
                        onChangeText={(value) => {
                          setFullName(value);
                          clearAuthError();
                        }}
                        placeholder="Harun Bulbul"
                        value={fullName}
                      />
                      <AuthInput
                        autoCapitalize="words"
                        label="School name"
                        onChangeText={(value) => {
                          setSchoolName(value);
                          clearAuthError();
                        }}
                        placeholder="Bright Future Academy"
                        value={schoolName}
                      />
                      <AuthButton onPress={handleNextStep} title="Next" />
                    </>
                  ) : step === 2 ? (
                    <>
                      <AuthInput
                        autoCapitalize="none"
                        autoComplete="email"
                        keyboardType="email-address"
                        label="Email"
                        onChangeText={(value) => {
                          setEmail(value);
                          clearAuthError();
                        }}
                        placeholder="admin@school.com"
                        value={email}
                      />
                      <AuthInput
                        autoCapitalize="none"
                        autoComplete="new-password"
                        label="Password"
                        onChangeText={(value) => {
                          setPassword(value);
                          clearAuthError();
                        }}
                        placeholder="Create a secure password"
                        secureTextEntry
                        value={password}
                      />
                      <AuthInput
                        autoCapitalize="none"
                        autoComplete="new-password"
                        label="Confirm password"
                        onChangeText={(value) => {
                          setConfirmPassword(value);
                          clearAuthError();
                        }}
                        placeholder="Repeat the password"
                        secureTextEntry
                        value={confirmPassword}
                      />
                      <View style={styles.actionGroup}>
                        <View style={styles.actionItem}>
                          <AuthButton onPress={() => setStep(1)} title="Back" variant="secondary" />
                        </View>
                        <View style={styles.actionItem}>
                          <AuthButton loading={loading} onPress={handleSignUp} title="Create account" />
                        </View>
                      </View>
                    </>
                  ) : (
                    <>
                      <AuthNote tone="neutral">
                        Enter the {EMAIL_OTP_LENGTH}-digit code sent to <Text style={styles.inlineEmail}>{email.trim()}</Text>.
                      </AuthNote>
                      <AuthInput
                        autoCapitalize="none"
                        autoComplete="one-time-code"
                        keyboardType="number-pad"
                        label="Email OTP"
                        onChangeText={(value) => {
                          setOtpCode(value.replace(/\D/g, '').slice(0, EMAIL_OTP_LENGTH));
                          clearAuthError();
                        }}
                        placeholder="12345678"
                        value={otpCode}
                      />
                      <View style={styles.actionGroup}>
                        <View style={styles.actionItem}>
                          <AuthButton onPress={() => setStep(2)} title="Back" variant="secondary" />
                        </View>
                        <View style={styles.actionItem}>
                          <AuthButton loading={loading} onPress={handleVerifyOtp} title="Confirm account" />
                        </View>
                      </View>
                      <AuthButton
                        loading={resendLoading}
                        onPress={handleResendOtp}
                        title="Resend OTP"
                        variant="secondary"
                      />
                    </>
                  )}

                  {authError ? <AuthNote tone="warn">{authError}</AuthNote> : null}

                  <AuthNote tone="neutral">
                    We use your school name to personalize the workspace and signup flow.
                  </AuthNote>

                  <View style={styles.desktopFooterRow}>
                    <Text style={styles.desktopFooterPrompt}>Already have an account?</Text>
                    <Link href="/sign-in" style={styles.desktopFooterAction}>
                      Sign in
                    </Link>
                  </View>

                  <Link href="/" style={styles.desktopFooterBack}>
                    Back to welcome
                  </Link>
                </View>
              </View>
            </View>
          </View>
        </ScrollView>
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  stepHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    marginBottom: 4,
  },
  stepNode: {
    alignItems: 'center',
    gap: 6,
  },
  stepPill: {
    width: 34,
    height: 34,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#E7EFE8',
    borderWidth: 1,
    borderColor: '#D4E0D8',
  },
  stepPillActive: {
    backgroundColor: '#163828',
    borderColor: '#163828',
  },
  stepPillText: {
    color: '#6A7D6F',
    fontSize: 13,
    fontWeight: '800',
  },
  stepPillTextActive: {
    color: '#FFFFFF',
  },
  stepLine: {
    width: 52,
    height: 1,
    backgroundColor: '#C9D5CB',
    marginBottom: 20,
  },
  stepLabel: {
    color: '#8A968B',
    fontSize: 11,
    fontWeight: '700',
    letterSpacing: 0.6,
  },
  stepLabelActive: {
    color: '#163828',
  },
  actionGroup: {
    flexDirection: 'row',
    gap: 12,
  },
  actionItem: {
    flex: 1,
  },
  secondaryLink: {
    alignSelf: 'center',
    color: '#6D8071',
    fontSize: 13,
    fontWeight: '600',
  },
  inlineEmail: {
    color: '#173924',
    fontWeight: '800',
  },
  desktopSafeArea: {
    flex: 1,
    backgroundColor: '#F5F1E8',
  },
  desktopBg: {
    flex: 1,
    backgroundColor: '#F5F1E8',
  },
  desktopGlowOrbOne: {
    position: 'absolute',
    width: 460,
    height: 460,
    borderRadius: 230,
    top: -150,
    left: -130,
    backgroundColor: 'rgba(202, 221, 204, 0.9)',
  },
  desktopGlowOrbTwo: {
    position: 'absolute',
    width: 520,
    height: 520,
    borderRadius: 260,
    right: -170,
    bottom: -170,
    backgroundColor: 'rgba(229, 217, 193, 0.78)',
  },
  desktopScroll: {
    flexGrow: 1,
    justifyContent: 'center',
    paddingHorizontal: 40,
    paddingVertical: 24,
  },
  desktopShell: {
    maxWidth: 1240,
    width: '100%',
    alignSelf: 'center',
  },
  desktopFrame: {
    width: '100%',
    minHeight: 640,
    flexDirection: 'row',
    borderRadius: 34,
    overflow: 'hidden',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#D9E3DA',
    shadowColor: '#1E2C22',
    shadowOffset: { width: 0, height: 24 },
    shadowOpacity: 0.16,
    shadowRadius: 38,
    elevation: 10,
  },
  desktopBrandPanel: {
    flex: 1.08,
    backgroundColor: '#173A2A',
    paddingHorizontal: 34,
    paddingVertical: 34,
    justifyContent: 'center',
  },
  desktopBrandRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
  },
  desktopBrandMark: {
    width: 68,
    height: 68,
    borderRadius: 20,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#D9E3DA',
    alignItems: 'center',
    justifyContent: 'center',
  },
  desktopLogo: {
    width: 52,
    height: 52,
  },
  desktopBrandCopy: {
    gap: 3,
  },
  desktopBrandKicker: {
    color: '#173A2A',
    fontSize: 22,
    fontFamily: Fonts.rounded,
    fontWeight: '900',
    letterSpacing: 0,
  },
  desktopBrandSub: {
    color: '#6B7D71',
    fontSize: 13,
    fontWeight: '600',
  },
  desktopHeadlineBlock: {
    gap: 4,
  },
  desktopEyebrow: {
    color: '#7A8C63',
    fontSize: 12,
    fontWeight: '900',
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  desktopTitle: {
    color: '#173A2A',
    fontSize: 28,
    lineHeight: 34,
    fontFamily: Fonts.rounded,
    fontWeight: '900',
    letterSpacing: 0,
  },
  desktopSubtitle: {
    fontSize: 14,
    lineHeight: 20,
    color: '#5F7166',
  },
  desktopFormPanel: {
    flex: 0.92,
    backgroundColor: '#F6F1E8',
    paddingHorizontal: 34,
    paddingVertical: 34,
    borderLeftWidth: 1,
    borderLeftColor: '#D9E3DA',
  },
  desktopStepHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    marginBottom: 16,
  },
  desktopCard: {
    width: '100%',
    height: '100%',
    gap: 18,
    justifyContent: 'center',
  },
  desktopCardHeader: {
    gap: 16,
    marginBottom: 8,
  },
  desktopFooterRow: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 4,
  },
  desktopFooterPrompt: {
    color: '#5F7166',
    fontSize: 13,
    fontWeight: '600',
  },
  desktopFooterAction: {
    color: '#163828',
    fontSize: 13,
    fontWeight: '800',
    textDecorationLine: 'underline',
  },
  desktopFooterBack: {
    alignSelf: 'flex-start',
    color: '#5F7166',
    fontSize: 13,
    fontWeight: '700',
    marginTop: 'auto',
  },
  desktopSliderStage: {
    flex: 1,
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
  },
  desktopSliderImageWrap: {
    flex: 1,
    width: '100%',
    alignSelf: 'stretch',
    aspectRatio: 0.75,
    borderRadius: 0,
    backgroundColor: 'transparent',
    borderWidth: 0,
    overflow: 'hidden',
  },
  desktopSliderImage: {
    width: '100%',
    height: '100%',
  },
  desktopSliderDots: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
  },
  desktopSliderDot: {
    width: 10,
    height: 10,
    borderRadius: 999,
    backgroundColor: 'rgba(247,243,235,0.36)',
  },
  desktopSliderDotActive: {
    width: 28,
    backgroundColor: '#F7F3EB',
  },
});
